import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Button, CircularProgress, Alert, Chip,
  IconButton, Tooltip, LinearProgress,
} from '@mui/material';
import { WhatsApp, Refresh, Print, AccountBalance } from '@mui/icons-material';
import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK') : '—';

function AgingBucket({ value, total, label, color }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography fontWeight={700} color={`${color}.main`}>₨ {fmt(value)}</Typography>
      <LinearProgress variant="determinate" value={Math.min(100, pct)} color={color}
        sx={{ height: 4, borderRadius: 2, mt: 0.5 }} />
    </Box>
  );
}

export default function AgingReportPage() {
  const dispatch = useDispatch();
  const printRef = useRef();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get('/reports/aging');
      setData(r.data);
    } catch (e) {
      dispatch(showSnackbar({ message: 'Failed to load aging report', severity: 'error' }));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sendReminder = async (customer) => {
    setSending(s => ({ ...s, [customer.id]: true }));
    try {
      const msg = `Dear ${customer.name}, your outstanding balance is ₨ ${fmt(customer.credit_balance)}. Kindly arrange payment at your earliest convenience. Thank you.`;
      await api.post('/whatsapp/send', { phone: customer.phone, message: msg });
      dispatch(showSnackbar({ message: `Reminder sent to ${customer.name}`, severity: 'success' }));
    } catch {
      dispatch(showSnackbar({ message: 'WhatsApp send failed', severity: 'error' }));
    } finally { setSending(s => ({ ...s, [customer.id]: false })); }
  };

  const sendBulkReminders = async () => {
    if (!data?.rows?.length) return;
    const withPhone = data.rows.filter(r => r.phone);
    for (const c of withPhone) { await sendReminder(c); }
  };

  const handlePrint = useReactToPrint({ contentRef: printRef });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  const totals = data?.totals;
  const rows = data?.rows || [];
  const grandTotal = +totals?.grand_total || 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalance color="error" />
          <Typography variant="h5" fontWeight={700}>Customer Aging Report</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>Print</Button>
          {rows.filter(r => r.phone).length > 0 && (
            <Button variant="contained" color="success"
              startIcon={<WhatsApp />}
              sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe57' } }}
              onClick={sendBulkReminders}>
              Send All Reminders ({rows.filter(r => r.phone).length})
            </Button>
          )}
        </Box>
      </Box>

      {/* Summary buckets */}
      {totals && grandTotal > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>Outstanding Summary — Total: ₨ {fmt(grandTotal)}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
              <AgingBucket value={+totals.bucket_30} total={grandTotal} label="0–30 days" color="success" />
              <AgingBucket value={+totals.bucket_60} total={grandTotal} label="31–60 days" color="warning" />
              <AgingBucket value={+totals.bucket_90} total={grandTotal} label="61–90 days" color="error" />
              <AgingBucket value={+totals.bucket_over90} total={grandTotal} label="90+ days" color="error" />
            </Box>
          </CardContent>
        </Card>
      )}

      {rows.length === 0 && !loading && (
        <Alert severity="success" icon={<AccountBalance />}>
          No outstanding balances — all customers are settled!
        </Alert>
      )}

      {rows.length > 0 && (
        <Card ref={printRef}>
          <CardContent>
            <style>{`@media print { @page { size: A4 landscape; margin: 10mm; } }`}</style>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Outstanding Receivables — {rows.length} customers — ₨ {fmt(grandTotal)}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'error.50' }}>
                  <TableCell fontWeight={700}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>0–30 days</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>31–60 days</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>61–90 days</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>90+ days</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Total Due</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last Sale</TableCell>
                  <TableCell className="no-print" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => {
                  const risk = +r.bucket_over90 > 0 ? 'error' : +r.bucket_90 > 0 ? 'warning' : 'default';
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                        {risk !== 'default' && (
                          <Chip label={risk === 'error' ? 'Overdue 90+' : 'Overdue 60+'} size="small" color={risk} sx={{ fontSize: 10, height: 16 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{r.phone || '—'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={+r.bucket_30 > 0 ? 'success.main' : 'text.disabled'}>
                          {+r.bucket_30 > 0 ? `₨ ${fmt(r.bucket_30)}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={+r.bucket_60 > 0 ? 'warning.main' : 'text.disabled'}>
                          {+r.bucket_60 > 0 ? `₨ ${fmt(r.bucket_60)}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={+r.bucket_90 > 0 ? 'error.main' : 'text.disabled'}>
                          {+r.bucket_90 > 0 ? `₨ ${fmt(r.bucket_90)}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={+r.bucket_over90 > 0 ? 'error.main' : 'text.disabled'} fontWeight={+r.bucket_over90 > 0 ? 700 : 400}>
                          {+r.bucket_over90 > 0 ? `₨ ${fmt(r.bucket_over90)}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={700} color="error.main">₨ {fmt(r.credit_balance)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{fmtDate(r.last_sale_at)}</Typography>
                      </TableCell>
                      <TableCell className="no-print">
                        {r.phone && (
                          <Tooltip title={`Send WhatsApp reminder to ${r.name}`}>
                            <IconButton
                              size="small"
                              disabled={sending[r.id]}
                              onClick={() => sendReminder(r)}
                              sx={{ color: '#25D366' }}
                            >
                              {sending[r.id] ? <CircularProgress size={16} /> : <WhatsApp fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Totals row */}
                <TableRow sx={{ bgcolor: 'error.50', fontWeight: 700 }}>
                  <TableCell colSpan={3}><Typography fontWeight={700}>TOTAL</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight={700} color="success.main">₨ {fmt(totals?.bucket_30)}</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight={700} color="warning.main">₨ {fmt(totals?.bucket_60)}</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight={700} color="error.main">₨ {fmt(totals?.bucket_90)}</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight={700} color="error.main">₨ {fmt(totals?.bucket_over90)}</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight={700} color="error.main">₨ {fmt(grandTotal)}</Typography></TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
